import { CONTENT_TYPE_BANNER } from '../request/TagBidContent.js';

/**
 * Sandbox permissions for rendered ad iframes.
 *
 * allow-scripts              — creatives require JS for click tracking, animation, etc.
 * allow-same-origin          — buyer creative JS may need to verify impression tracking URLs.
 *                              Acceptable because we control all creatives served by the exchange.
 * allow-popups               — click-through to advertiser landing pages
 * allow-popups-to-escape-sandbox — windows opened by the creative are not themselves sandboxed
 */
const IFRAME_SANDBOX =
  'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox';

/**
 * Wraps raw creative markup in a complete HTML document for use as srcdoc.
 * Resets browser default margin/padding so creative renders flush to the frame edges.
 *
 * color-scheme is pinned to light here and on the iframe element in
 * createBannerIframe. If the iframe element and embedded document resolve
 * different color schemes, browsers can paint an opaque canvas behind the
 * frame; on dark publisher pages that shows through around rounded creatives.
 *
 * @param {string} markup
 * @returns {string}
 */
function buildSrcdoc(markup) {
  return (
    '<!DOCTYPE html>' +
    '<html><head><meta charset="utf-8">' +
    '<style>' +
    '* { margin: 0; padding: 0; box-sizing: border-box }' +
    ':root { color-scheme: light }' +
    'html, body { width: 100%; height: 100%; overflow: hidden; background: transparent }' +
    '</style></head>' +
    `<body>${markup}</body></html>`
  );
}

/**
 * Creates a sandboxed srcdoc iframe sized to the given pixel dimensions.
 *
 * display:block on the iframe eliminates the baseline gap that appears
 * when an inline element sits inside a block container.
 *
 * @param {string} markup
 * @param {number} w  - declared creative width in pixels
 * @param {number} h  - declared creative height in pixels
 * @returns {HTMLIFrameElement}
 */
function createBannerIframe(markup, w, h, ownerDocument) {
  const iframe = ownerDocument.createElement('iframe');

  iframe.setAttribute('sandbox', IFRAME_SANDBOX);
  iframe.setAttribute('scrolling', 'no');
  iframe.setAttribute('frameborder', '0');
  iframe.width        = String(w);
  iframe.height       = String(h);
  iframe.style.display  = 'block';
  iframe.style.border   = 'none';
  iframe.style.background = 'transparent';
  // Must match the :root color-scheme in buildSrcdoc — see its doc comment.
  iframe.style.colorScheme = 'light';
  // Mirror the wrapper's responsive cap (placement.js): the iframe shrinks
  // with narrow viewports up to its declared width, so creatives that scale
  // gracefully (chattable) render correctly on small slots. Fixed-size
  // banner images inside an over-narrow iframe overflow as before — that's
  // a property of the creative, not changed by this rule.
  iframe.style.width    = '100%';
  iframe.style.maxWidth = `${w}px`;
  iframe.style.height   = `${h}px`;
  iframe.srcdoc       = buildSrcdoc(markup);

  return iframe;
}

/**
 * Renders creative content into the provided wrapper element.
 *
 * Does NOT insert the wrapper into the DOM — that is insertWrapper's responsibility.
 *
 * Rendering decision:
 *
 *   needsIframe = !renderEnv.inIframe && !requiresDirectSurface
 *
 *   Context              | Allocated/fixed          | Surface-aware
 *   ---------------------|--------------------------|------------------------------
 *   On-page              | srcdoc iframe            | direct inject into wrapper
 *   Any framed context   | direct inject            | direct inject; the outer
 *                        |                          | SurfaceController owns sizing
 *
 * srcdoc iframe is only used for on-page isolated banners — the one case
 * where creative markup would otherwise run directly in the publisher page with
 * no isolation boundary.
 *
 * For all direct-inject paths the creative's own CSS fully controls sizing.
 * The wrapper carries no explicit dimensions and does not constrain the creative.
 *
 * Only CONTENT_TYPE_BANNER is supported in v1. Video and audio throw — the error
 * is caught by the handlePlacement error boundary.
 *
 * @param {import('../request/TagBidContent.js').TagBidContent} content
 * @param {boolean} requiresDirectSurface
 * @param {object} renderEnv   - from getRenderEnv()
 * @param {HTMLElement} wrapper
 * @throws {Error} if content type is not CONTENT_TYPE_BANNER
 */
export function renderAd(content, requiresDirectSurface, renderEnv, wrapper) {
  if (content.type !== CONTENT_TYPE_BANNER) {
    throw new Error(
      `Content type '${content.type}' is not yet supported — only banner creatives can be rendered in v1`
    );
  }

  const needsIframe = !renderEnv.inIframe && !requiresDirectSurface;

  if (needsIframe) {
    const iframe = createBannerIframe(content.content, content.w, content.h, wrapper.ownerDocument);

    wrapper.appendChild(iframe);

    return;
  }

  injectMarkup(wrapper, content.content);
}

/**
 * Injects HTML markup into a container, ensuring embedded <script> tags
 * execute. Uses createContextualFragment (widely supported) which parses
 * HTML and runs scripts on append — unlike innerHTML which silently
 * drops script execution.
 *
 * Falls back to innerHTML for environments where Range is unavailable
 * (e.g. some embedded webviews). Scripts won't run in that case, but
 * non-script content still renders.
 *
 * @param {HTMLElement} container
 * @param {string} markup
 */
function injectMarkup(container, markup) {
  const ownerDocument = container.ownerDocument;
  if (typeof ownerDocument.createRange === 'function') {
    const range = ownerDocument.createRange();
    range.selectNodeContents(container);
    container.appendChild(range.createContextualFragment(markup));
  } else {
    container.innerHTML = markup;
  }
}
